var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import AppConfig from '../../config/config';
import { mapProducts, mapFilters, mapMeta, mapSorts } from './mapper';
import { fetchBoost, fetchBoostRecs, waitForBoostWidgetIntegrationReady } from './api';
export class BoostSearchProvider {
    constructor() {
        this.products = [];
        this.recs = [];
        this.filters = [];
        this.meta = null;
        this.sortOptions = [];
    }
    search(query_1) {
        return __awaiter(this, arguments, void 0, function* (query, options = {}) {
            var _a, _b, _c;
            const { page = 1, pageSize = AppConfig.page.defaultSize, sort = 'relevance', filters, merchandising } = options;
            try {
                // Format price filter if it exists
                const formattedFilters = filters ? Object.assign({}, filters) : undefined;
                if (formattedFilters === null || formattedFilters === void 0 ? void 0 : formattedFilters.pf_p_price) {
                    // Ensure price filter is in the correct format (min:max)
                    formattedFilters.pf_p_price = formattedFilters.pf_p_price.map(value => {
                        // If value already contains ':', return as is
                        if (value.includes(':'))
                            return value;
                        // Otherwise, replace ',' with ':'
                        return value.replace(',', ':');
                    });
                }
                // Convert MerchandisingConfig to Boost's expected format
                const boostMerchandising = merchandising ? {
                    applyRules: merchandising.enableRules,
                    boostProducts: (_a = merchandising.boosts) === null || _a === void 0 ? void 0 : _a.map(b => b.productId),
                    hideProducts: ((_b = merchandising.merchandisingFilters) === null || _b === void 0 ? void 0 : _b.includes('hide:'))
                        ? merchandising.merchandisingFilters.split('hide:')[1].split(',')
                        : undefined,
                    pinProducts: (_c = merchandising.pins) === null || _c === void 0 ? void 0 : _c.map(p => ({
                        productId: p.productId,
                        position: typeof p.position === 'number' ? p.position : p.position.start
                    }))
                } : undefined;
                const data = yield fetchBoost({
                    q: query,
                    page,
                    pageSize,
                    sort,
                    filters: formattedFilters,
                    merchandising: boostMerchandising,
                    fuzzy: true, // Enable typo tolerance by default
                });
                this.products = mapProducts(data.products);
                this.filters = mapFilters(data.filter, formattedFilters);
                this.meta = mapMeta(data, page, pageSize);
                this.sortOptions = mapSorts();
            }
            catch (error) {
                throw new Error('Failed to load mock data');
            }
        });
    }
    fetchRecs(seedProductId_1, recsType_1) {
        return __awaiter(this, arguments, void 0, function* (seedProductId, recsType, options = {}, signal) {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            // Cache key must discriminate across recsTypes and widgets so a "bestsellers"
            // response doesn't shadow a "related-items" response for the same PDP.
            const cacheKey = `${recsType || 'default'}|${options.widgetId || ''}|${seedProductId || ''}`;
            try {
                if (!window.mrRecommendations) {
                    window.mrRecommendations = {};
                }
                if (!window.mrRecommendations.productDetails) {
                    window.mrRecommendations.productDetails = {};
                }
                let effectiveOptions = options;
                if (options.configSource === 'boost-app') {
                    // BoostApp mode: ignore the Shopify section fields (the merchant chose
                    // "use Boost admin settings"), wait for the bundle to populate its
                    // config, and pull `params` for this widget. `widgetId` / `recsType`
                    // still come from the caller — they identify which widget to look up.
                    const ready = yield waitForBoostWidgetIntegrationReady();
                    const widgets = (_b = (_a = window.boostWidgetIntegration) === null || _a === void 0 ? void 0 : _a.config) === null || _b === void 0 ? void 0 : _b.recommendationWidgets;
                    // The outer key of `recommendationWidgets` is the kebab-cased page type
                    // (`home-page`, `product-page`, …), but `options.pageType` carries the
                    // snake-cased form (`home_page`) because that's what the Boost API
                    // expects in the `pg` query param. Convert before the lookup.
                    const widgetPageKey = (_c = options.pageType) === null || _c === void 0 ? void 0 : _c.replace(/_/g, '-');
                    const params = ready && widgetPageKey && options.widgetId
                        ? (_e = (_d = widgets === null || widgets === void 0 ? void 0 : widgets[widgetPageKey]) === null || _d === void 0 ? void 0 : _d[options.widgetId]) === null || _e === void 0 ? void 0 : _e.params
                        : undefined;
                    if (!params) {
                        console.error('[BoostSearchProvider] BoostApp config unavailable for', { pageType: options.pageType, widgetPageKey, widgetId: options.widgetId, ready, boostWidgetIntegration: window.boostWidgetIntegration });
                    }
                    else {
                        // Drop `recommendationType` and `shop` from params — `recsType` is the
                        // authoritative source and feeds the cache key, and `shop` is already
                        // sourced from AppConfig inside fetchBoostRecs.
                        // Caller options form the base so contextual fields not present in
                        // BoostApp `params` (pageType for `pg`, locale, customerId, etc.)
                        // survive; `params` then overrides the Boost-config-driven fields
                        // (limit, layoutDisplay, modelType, rules, …). `widgetId` is forced
                        // back to the caller's value as a paranoid guard against a stray
                        // `params.widgetId` desynchronising the cache key.
                        const { recommendationType: _ignored, shop: _ignoredShop } = params, paramsForRequest = __rest(params, ["recommendationType", "shop"]);
                        effectiveOptions = Object.assign(Object.assign(Object.assign({}, options), paramsForRequest), { widgetId: options.widgetId });
                    }
                }
                const limit = (_f = effectiveOptions.limit) !== null && _f !== void 0 ? _f : AppConfig.page.defaultSize;
                let data;
                if (window.mrRecommendations.productDetails[cacheKey]) {
                    data = window.mrRecommendations.productDetails[cacheKey];
                }
                else {
                    data = yield fetchBoostRecs(Object.assign(Object.assign({}, effectiveOptions), { seedProductId,
                        recsType,
                        limit }), signal);
                    window.mrRecommendations.productDetails[cacheKey] = data;
                }
                this.recs = mapProducts(data.products || []);
                this.meta = mapMeta(data, 1, limit);
            }
            catch (error) {
                // AbortError is an expected control-flow signal, not a failure. Re-throw
                // it as-is (preserving the `name === 'AbortError'`) so `useRecsProvider`
                // can swallow it without logging. Wrapping it in a fresh Error here
                // would mask the name and trigger the noisy
                // "AbortError: signal is aborted without reason" log on every
                // superseded fetch (e.g. retargeter racing the first-open fetch).
                if ((error === null || error === void 0 ? void 0 : error.name) === 'AbortError')
                    throw error;
                if ((_h = (_g = window.mrRecommendations) === null || _g === void 0 ? void 0 : _g.productDetails) === null || _h === void 0 ? void 0 : _h[cacheKey]) {
                    delete window.mrRecommendations.productDetails[cacheKey];
                }
                console.error('[BoostSearchProvider] Failed to fetch recommendations:', error);
                throw new Error('[BoostSearchProvider] Failed to load recommendations');
            }
        });
    }
    /** Apply a raw Boost response from `early-init-boost.js` (see
     *  `src/search/preload.ts`) through the same post-fetch steps as
     *  `search()`, skipping the network call. Throws on a non-Boost shape so
     *  the caller can fall back to a live `search()`. */
    setPreloadedData(rawData, options = {}) {
        const data = rawData;
        if (!data || !Array.isArray(data.products)) {
            throw new Error('setPreloadedData: rawData is not a Boost search/filter response');
        }
        const { page = 1, pageSize = AppConfig.page.defaultSize, filters } = options;
        // Mirrors search()'s price-filter normalization (comma -> colon).
        const formattedFilters = filters ? Object.assign({}, filters) : undefined;
        if (formattedFilters === null || formattedFilters === void 0 ? void 0 : formattedFilters.pf_p_price) {
            formattedFilters.pf_p_price = formattedFilters.pf_p_price.map(value => value.includes(':') ? value : value.replace(',', ':'));
        }
        this.products = mapProducts(data.products);
        this.filters = mapFilters(data.filter, formattedFilters);
        this.meta = mapMeta(data, page, pageSize);
        this.sortOptions = mapSorts();
    }
    getProducts() {
        return this.products;
    }
    getRecs() {
        return this.recs;
    }
    getFilters() {
        if (!Array.isArray(this.filters))
            return this.filters;
        // Find "Price" filter and move to first
        const filters = [...this.filters];
        const priceIndex = filters.findIndex(f => f.label === 'Price');
        if (priceIndex > 0) {
            const [priceFilter] = filters.splice(priceIndex, 1);
            filters.unshift(priceFilter);
        }
        return filters;
    }
    getMeta() {
        if (!this.meta) {
            throw new Error('No search has been performed yet');
        }
        return this.meta;
    }
    getSortOptions() {
        return this.sortOptions;
    }
}
